import classNames from 'classnames';
import type { MouseEvent } from 'react';
import { memo, useState } from 'react';

import { Button } from '@revelio/ui';

import styles from './BlurredCell.module.css';
import { useTrialUnblur } from './TrialUnblurProvider/TrialUnblurProvider';

interface BlurredCellProps {
  value: string;
  encryptedValue?: string;
  revealedValue?: string;
  userKey?: string;
  fieldKey?: string;
  onMouseEnter: () => void;
  allowUnblur?: boolean;
  isCompact?: boolean;
  'data-testid'?: string;
}

const BlurredCell = memo(function BlurredCell({
  value,
  encryptedValue,
  revealedValue,
  userKey,
  fieldKey,
  onMouseEnter,
  allowUnblur,
  isCompact = true,
  'data-testid': testId,
}: BlurredCellProps) {
  const trialUnblur = useTrialUnblur();
  const [isHovered, setIsHovered] = useState(false);
  const hasEncryptedValue =
    typeof encryptedValue === 'string' && encryptedValue.trim().length > 0;
  const decryptedValue =
    userKey && fieldKey && trialUnblur
      ? trialUnblur.getDecryptedValue({ userKey, fieldKey })
      : undefined;
  const resolvedRevealedValue = revealedValue ?? decryptedValue;
  const isRevealed = Boolean(resolvedRevealedValue);
  const isDecrypting =
    userKey && fieldKey && trialUnblur
      ? trialUnblur.isDecrypting({ userKey, fieldKey })
      : false;
  const shouldShowUnblurButton = Boolean(
    allowUnblur && !isRevealed && hasEncryptedValue && isHovered
  );

  const handleUnblur = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const canRequestDecrypt = Boolean(
      allowUnblur && hasEncryptedValue && trialUnblur && userKey && fieldKey
    );
    if (!canRequestDecrypt || !trialUnblur || !userKey || !fieldKey) return;
    void trialUnblur.requestDecrypt({
      userKey,
      fieldKey,
      encryptedValue: encryptedValue ?? '',
    });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    onMouseEnter();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      className={styles.cell}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={testId}
    >
      {isRevealed ? (
        <div className={styles.cell}>{resolvedRevealedValue}</div>
      ) : (
        <div
          className={classNames(styles.cell, styles.blurred)}
          data-compact={isCompact}
          data-testid="blurred-cell"
        >
          {value}
        </div>
      )}

      {shouldShowUnblurButton && (
        <Button
          size="xs"
          className={styles.unblurButton}
          onClick={handleUnblur}
          onMouseDown={(event) => event.stopPropagation()}
          loading={isDecrypting}
          loadingText=""
        >
          Unblur
        </Button>
      )}
    </div>
  );
});

export default BlurredCell;
